import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Typography,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tab,
    Tabs,
    Box,
    Paper,
    Snackbar,
    Alert,
    Grid,
    Card,
    CardContent,
    CardActions,
    InputAdornment,
    Chip,
    Divider,
    LinearProgress,
    Tooltip,
    Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NoteIcon from '@mui/icons-material/Note';

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[8],
    },
}));

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [openAdminDialog, setOpenAdminDialog] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [openNotesDialog, setOpenNotesDialog] = useState(false);
    const [notesUser, setNotesUser] = useState(null);
    const [pinnedNote, setPinnedNote] = useState('');
    const [noteLog, setNoteLog] = useState([]);
    const [newLogEntry, setNewLogEntry] = useState('');
    const [notesLoading, setNotesLoading] = useState(false);
    const [savingNote, setSavingNote] = useState(false);
    const [addingLogEntry, setAddingLogEntry] = useState(false);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(1); // Reset to first page when search changes
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch users when page or search changes
    useEffect(() => {
        fetchUsers();
    }, [page, debouncedSearchTerm]); // eslint-disable-line

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/users`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        page,
                        limit: 20,
                        search: debouncedSearchTerm,
                    },
                },
            );
            setUsers(response.data.data.users);
            setTotalPages(response.data.totalPages || 1);
            setTotalUsers(response.data.totalUsers || 0);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError(
                'An error occurred while fetching users. Please try again.',
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (userData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/users`,
                userData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            fetchUsers();
            showSnackbar('User created successfully', 'success');
        } catch (error) {
            console.error('Error creating user:', error);
            showSnackbar('Error creating user', 'error');
        }
    };

    const handleUpdateUser = async (id, userData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/users/${id}`,
                userData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            fetchUsers();
            showSnackbar('User updated successfully', 'success');
        } catch (error) {
            console.error('Error updating user:', error);
            showSnackbar('Error updating user', 'error');
        }
    };

    const handleDeleteUser = async (id) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/api/users/${id}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    },
                );
                fetchUsers();
                showSnackbar('User deleted successfully', 'success');
            } catch (error) {
                console.error('Error deleting user:', error);
                showSnackbar('Error deleting user', 'error');
            }
        }
    };

    const handleSendPasswordReset = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/users/sendPasswordReset/${userId}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            showSnackbar('Password reset email sent', 'success');
        } catch (error) {
            console.error('Error sending password reset:', error);
            showSnackbar('Error sending password reset', 'error');
        }
    };

    const handleCreateAdmin = async (adminData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/users/createAdmin`,
                adminData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            console.log('Admin creation response:', response.data);
            showSnackbar('Admin account created successfully', 'success');
            setOpenAdminDialog(false);
        } catch (error) {
            console.error('Error creating admin account:', error);
            console.error('Error response:', error.response);
            showSnackbar(
                `Error creating admin account: ${error.response?.data?.message || error.message}`,
                'error',
            );
        }
    };

    const handleOpenDialog = (user = null) => {
        setCurrentUser(user);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentUser(null);
    };

    const handleSubmitDialog = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const userData = Object.fromEntries(formData.entries());

        if (currentUser) {
            handleUpdateUser(currentUser._id, userData);
        } else {
            handleCreateUser(userData);
        }
        handleCloseDialog();
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmitAdminDialog = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const adminData = Object.fromEntries(formData.entries());
        handleCreateAdmin(adminData);
    };

    const handleOpenAdminDialog = () => {
        setOpenAdminDialog(true);
    };

    const handleCloseAdminDialog = () => {
        setOpenAdminDialog(false);
    };

    const fetchUserNotes = async (userId) => {
        try {
            setNotesLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/users/${userId}/notes`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPinnedNote(response.data.data.pinnedNote || '');
            setNoteLog(response.data.data.noteLog || []);
        } catch (err) {
            console.error('Error fetching notes:', err);
            setSnackbar({ open: true, message: 'Failed to load notes', severity: 'error' });
        } finally {
            setNotesLoading(false);
        }
    };

    const handleSavePinnedNote = async () => {
        if (!notesUser) return;
        try {
            setSavingNote(true);
            const token = localStorage.getItem('token');
            await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/users/${notesUser._id}/notes`,
                { pinnedNote },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({ open: true, message: 'Note saved', severity: 'success' });
        } catch (err) {
            console.error('Error saving pinned note:', err);
            setSnackbar({ open: true, message: 'Failed to save note', severity: 'error' });
        } finally {
            setSavingNote(false);
        }
    };

    const handleAddLogEntry = async () => {
        if (!notesUser || !newLogEntry.trim()) return;
        try {
            setAddingLogEntry(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/users/${notesUser._id}/notes/log`,
                { text: newLogEntry.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setNoteLog((prev) => [response.data.data.entry, ...prev]);
            setNewLogEntry('');
        } catch (err) {
            console.error('Error adding log entry:', err);
            setSnackbar({ open: true, message: 'Failed to add note', severity: 'error' });
        } finally {
            setAddingLogEntry(false);
        }
    };

    const handleOpenNotes = (user) => {
        setNotesUser(user);
        setOpenNotesDialog(true);
        fetchUserNotes(user._id);
    };

    const renderNotesDialog = () => (
        <Dialog open={openNotesDialog} onClose={() => setOpenNotesDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                Notes for {notesUser?.firstName} {notesUser?.lastName}
            </DialogTitle>
            <DialogContent>
                {notesLoading ? (
                    <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 2 }} />
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Pinned Note
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={pinnedNote}
                            onChange={(e) => setPinnedNote(e.target.value)}
                            placeholder="General notes about this customer..."
                            sx={{ mb: 1 }}
                        />
                        <Button
                            onClick={handleSavePinnedNote}
                            variant="outlined"
                            size="small"
                            disabled={savingNote}
                            sx={{ mb: 3 }}
                        >
                            {savingNote ? 'Saving...' : 'Save Pinned Note'}
                        </Button>

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Activity Log
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={newLogEntry}
                                onChange={(e) => setNewLogEntry(e.target.value)}
                                placeholder="Add a note..."
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddLogEntry();
                                    }
                                }}
                            />
                            <Button
                                onClick={handleAddLogEntry}
                                variant="contained"
                                size="small"
                                disabled={addingLogEntry || !newLogEntry.trim()}
                            >
                                {addingLogEntry ? '...' : 'Add'}
                            </Button>
                        </Box>

                        {noteLog.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No log entries yet.
                            </Typography>
                        ) : (
                            noteLog.map((entry, index) => (
                                <Box key={entry._id || index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(entry.createdAt).toLocaleString()} - {entry.createdBy}
                                    </Typography>
                                    <Typography variant="body2">{entry.text}</Typography>
                                </Box>
                            ))
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenNotesDialog(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 2 }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{ textAlign: 'center', color: '#1976d2' }}
            >
                User Management
            </Typography>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} centered>
                    <Tab label="User List" />
                    <Tab label="Create User" />
                    <Tab label="Admin Functions" />
                </Tabs>
            </Paper>

            {tabValue === 0 && (
                <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search users by name or email..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography sx={{ ml: 2, minWidth: 150, textAlign: 'right' }} variant="body2" color="textSecondary">
                            Total: {totalUsers} users
                        </Typography>
                    </Box>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={3}>
                                {users.map((user) => (
                            <Grid item xs={12} sm={6} md={4} key={user._id}>
                                <StyledCard>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="h6">{`${user.firstName} ${user.lastName}`}</Typography>
                                            <Chip
                                                label={
                                                    user.isAdmin
                                                        ? 'Admin'
                                                        : 'Customer'
                                                }
                                                color={
                                                    user.isAdmin
                                                        ? 'secondary'
                                                        : 'primary'
                                                }
                                                size="small"
                                            />
                                        </Box>
                                        <Typography color="textSecondary" variant="body2" sx={{ mb: 2 }}>
                                            {user.email}
                                        </Typography>

                                        <Divider sx={{ my: 2 }} />

                                        {/* Practice Statistics */}
                                        <Box sx={{ mb: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <TrendingUpIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Current Stats
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    (Can be reset by user)
                                                </Typography>
                                            </Box>

                                            {user.totalQuestionsAnswered > 0 ? (
                                                <>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Accuracy
                                                        </Typography>
                                                        <Chip
                                                            label={`${user.accuracy}%`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: user.accuracy >= 70 ? '#e8f5e9' : '#fff3e0',
                                                                color: user.accuracy >= 70 ? '#2e7d32' : '#f57c00',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={user.accuracy}
                                                        sx={{
                                                            height: 6,
                                                            borderRadius: 3,
                                                            mb: 2,
                                                            backgroundColor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': {
                                                                backgroundColor: user.accuracy >= 70 ? '#4caf50' : '#ff9800',
                                                                borderRadius: 3,
                                                            },
                                                        }}
                                                    />
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center">
                                                                <Typography variant="caption" color="textSecondary" display="block">
                                                                    Total
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {user.totalQuestionsAnswered}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center">
                                                                <Tooltip title="Correct Answers">
                                                                    <Box>
                                                                        <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                                                            {user.totalCorrectAnswers}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center">
                                                                <Tooltip title="Incorrect Answers">
                                                                    <Box>
                                                                        <CancelIcon sx={{ fontSize: 14, color: '#f44336' }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#f44336' }}>
                                                                            {user.totalIncorrectAnswers}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    {/* Time Spent Display */}
                                                    {user.totalTimeSpent > 0 && (
                                                        <Box
                                                            sx={{
                                                                mt: 2,
                                                                pt: 2,
                                                                borderTop: '1px solid #e0e0e0',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 1
                                                            }}
                                                        >
                                                            <AccessTimeIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                                                            <Typography variant="body2" color="textSecondary">
                                                                Total Time:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                                                                {user.formattedTimeSpent || '0m'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                    No practice activity yet
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Lifetime Statistics - Admin Only */}
                                        {(user.lifetimeQuestionsAnswered > 0 || user.totalQuestionsAnswered > 0) && (
                                            <>
                                                <Divider sx={{ my: 2 }} />
                                                <Box sx={{ mb: 1 }}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                        <TrendingUpIcon sx={{ fontSize: 18, color: '#ff5e1c' }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ff5e1c' }}>
                                                            Lifetime Stats
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            (Never reset)
                                                        </Typography>
                                                    </Box>

                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Accuracy
                                                        </Typography>
                                                        <Chip
                                                            label={`${user.lifetimeAccuracy || 0}%`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: (user.lifetimeAccuracy || 0) >= 70 ? '#e8f5e9' : '#fff3e0',
                                                                color: (user.lifetimeAccuracy || 0) >= 70 ? '#2e7d32' : '#f57c00',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>

                                                    <Grid container spacing={1}>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center">
                                                                <Typography variant="caption" color="textSecondary" display="block">
                                                                    Total
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {user.lifetimeQuestionsAnswered || 0}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center">
                                                                <Tooltip title="Correct Answers">
                                                                    <Box>
                                                                        <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                                                            {user.lifetimeCorrectAnswers || 0}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box textAlign="center">
                                                                <Tooltip title="Incorrect Answers">
                                                                    <Box>
                                                                        <CancelIcon sx={{ fontSize: 14, color: '#f44336' }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#f44336' }}>
                                                                            {user.lifetimeIncorrectAnswers || 0}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    {/* Lifetime Time Spent Display */}
                                                    {(user.lifetimeTimeSpent > 0 || user.totalTimeSpent > 0) && (
                                                        <Box
                                                            sx={{
                                                                mt: 2,
                                                                pt: 2,
                                                                borderTop: '1px solid #e0e0e0',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 1
                                                            }}
                                                        >
                                                            <AccessTimeIcon sx={{ fontSize: 16, color: '#ff5e1c' }} />
                                                            <Typography variant="body2" color="textSecondary">
                                                                Total Time:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#ff5e1c' }}>
                                                                {user.formattedLifetimeTimeSpent || '0m'}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </CardContent>
                                    <CardActions>
                                        <IconButton
                                            onClick={() =>
                                                handleOpenDialog(user)
                                            }
                                            size="small"
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteUser(user._id)
                                            }
                                            size="small"
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() =>
                                                handleSendPasswordReset(
                                                    user._id,
                                                )
                                            }
                                            size="small"
                                            color="secondary"
                                        >
                                            <LockResetIcon />
                                        </IconButton>
                                        <Tooltip title="Notes">
                                            <IconButton
                                                onClick={() => handleOpenNotes(user)}
                                                color="info"
                                                size="small"
                                            >
                                                <NoteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </StyledCard>
                            </Grid>
                                ))}
                            </Grid>
                            {!debouncedSearchTerm && totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}

            {tabValue === 1 && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mb: 2 }}
                    >
                        Create New User
                    </Button>
                </Box>
            )}

            {tabValue === 2 && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleOpenAdminDialog}
                        sx={{ mb: 2 }}
                    >
                        Create Admin Account
                    </Button>
                </Box>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <form onSubmit={handleSubmitDialog}>
                    <DialogTitle>
                        {currentUser ? 'Edit User' : 'Create New User'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="firstName"
                            label="First Name"
                            type="text"
                            fullWidth
                            defaultValue={currentUser?.firstName || ''}
                        />
                        <TextField
                            margin="dense"
                            name="lastName"
                            label="Last Name"
                            type="text"
                            fullWidth
                            defaultValue={currentUser?.lastName || ''}
                        />
                        <TextField
                            margin="dense"
                            name="email"
                            label="Email Address"
                            type="email"
                            fullWidth
                            defaultValue={currentUser?.email || ''}
                        />
                        {!currentUser && (
                            <TextField
                                margin="dense"
                                name="password"
                                label="Password"
                                type="password"
                                fullWidth
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            {currentUser ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={openAdminDialog} onClose={handleCloseAdminDialog}>
                <form onSubmit={handleSubmitAdminDialog}>
                    <DialogTitle>Create Admin Account</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="firstName"
                            label="First Name"
                            type="text"
                            fullWidth
                            required
                        />
                        <TextField
                            margin="dense"
                            name="lastName"
                            label="Last Name"
                            type="text"
                            fullWidth
                            required
                        />
                        <TextField
                            margin="dense"
                            name="email"
                            label="Email Address"
                            type="email"
                            fullWidth
                            required
                        />
                        <TextField
                            margin="dense"
                            name="password"
                            label="Password"
                            type="password"
                            fullWidth
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAdminDialog}>Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            Create Admin
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {renderNotesDialog()}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Users;
